import { getter } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Point } from '../../../platform/geom/Point';
import { KeyExpression } from '../../../platform/input/KeyExpression';
import { KeyInput } from '../../../platform/input/KeyInput';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { WheelDevice, WheelGesture, WheelInput } from '../../../platform/input/WheelInput';
import { WheelUtil } from '../../../platform/input/WheelUtil';
import { resolve } from '../../Container';
import { PreferencesModel } from '../../preferences/PreferencesModel';
import { Camera } from '../Camera';
import { cameraMoveBy, cameraMoveTo, cameraMoveToOrigin, cameraMoveToStart, cameraZoomBy } from '../commands/CameraCommands';
import { InteractionHelper } from '../InteractionHelper';
import { KeyMappings } from './KeyMappings';
import { ToolBehavior } from './ToolBehavior';

const PanExpr = KeyExpression.parse('SPACE');
const MouseId = 1;

export interface PanZoomToolBehaviorConfig { hasPriority?: boolean, preventPan?: boolean, preventZoom?: boolean }

abstract class AbstractPanZoomToolBehavior extends ToolBehavior {

    @resolve(Camera) public camera!: Camera;
    @resolve(CommandDispatcher) public dispatch!: CommandDispatcher;
    @resolve(PreferencesModel) private preferences!: PreferencesModel;
    @resolve(InteractionHelper) private interaction!: InteractionHelper;

    private ptrId: number | null = null;
    private ptrStart: Point | null = null;
    private camPositionStart: Point | null = null;
    private isPanning = false;
    private isPanKeyPressed: boolean = false;

    public getKeyMappings = getter(KeyMappings.from(
        ['CTRL+KEY_H', cameraMoveToStart],
        ['KEY_H', cameraMoveToOrigin],
    ));

    constructor(private config: PanZoomToolBehaviorConfig) {
        super();
    }

    public getPreferredCursor(state: string) {
        if (state === 'panningPrimed') return 'handOpen';
        if (state === 'panning' && this.isPanning) return 'handGrab';
        return this.config.hasPriority ? 'handOpen' : null;
    }

    public onKeyDown(state: string, ki: KeyInput) {
        if (PanExpr.matches(ki)) {
            this.isPanKeyPressed = true;
            if (state === 'idle') {
                return 'panningPrimed';
            }
        }
    }

    public onKeyUp(state: string, ki: KeyInput) {
        if (PanExpr.matches(ki)) {
            this.isPanKeyPressed = false;
            if (state === 'panningPrimed') {
                return 'idle';
            }
        }
    }

    public onMouseDown(state: string, mi: MouseInput) {
        if (this.shouldPanBegin(state, mi)) {
            this.doPanBegin(MouseId, mi.position);
            // If panningPrimed, set isPanning to true here (this is for the cursor)
            this.isPanning = state === 'panningPrimed';
            return 'panning';
        }
    }

    public onMouseMove(state: string, mi: MouseInput) {
        if (state === 'panning') {
            this.doPanContinue(MouseId, mi.position);
        }
    }

    public onMouseUp(state: string, _mi: MouseInput) {
        if (state === 'panning') {
            if (this.doPanEnd(MouseId)) {
                return this.isPanKeyPressed ? 'panningPrimed' : 'idle';
            }
        }
    }

    public onWheel(state, wi: WheelInput) {
        const { config, dispatch, preferences } = this;
        if (wi.modifiers.shift) return;

        const normalizedDevice = preferences.mouseMode === 'auto' ? wi.device :
            (preferences.mouseMode === 'mouse' ? WheelDevice.Mouse : WheelDevice.Trackpad);

        if (normalizedDevice === WheelDevice.Trackpad) {
            if (wi.gesture === WheelGesture.Pinch) {
                if (!config.preventZoom) {
                    dispatch(cameraZoomBy, [wi.delta.y * -0.1, wi.position.toArray()]);
                }
            }
            else {
                if (!config.preventPan) {
                    dispatch(cameraMoveBy, wi.delta.toArray());
                }
            }
        }
        else {
            if (!config.preventZoom) {
                dispatch(cameraZoomBy, [wi.delta.y < 1 ? 1.5 : -1.5, wi.position.toArray()]);
            }
        }
    }

    public onWheelX(state: string, e: WheelEvent) {
        const { config, dispatch } = this;

        const info = WheelUtil.inspect(e);
        const at = Point.from(e, 'client');
        const delta = new Point(
            info.hints.shouldScale ? e.deltaX * 10 : e.deltaX,
            info.hints.shouldScale ? e.deltaY * 10 : e.deltaY,
        );

        if (info.source === 'trackpad') {
            // if (info.hints.pinch) {
            //     if (!config.preventZoom) {
            //         session.do('cameraZoom2', delta.y * -0.1, at);
            //     }
            // }
            // else {
            //     if (!config.preventPan) {
            //         session.do('cameraMove', delta);
            //     }
            // }
        }
        else {
            if (!config.preventZoom) {
                // Mouse delta.y can vary wildly so we normalize to a device agnostic constant value
                dispatch(cameraZoomBy, [delta.y < 1 ? 1.5 : -1.5, at.toArray()]);
            }
        }
    }

    private shouldPanBegin(state: string, mi: MouseInput): boolean {
        if (state === 'panningPrimed') return true;
        if (state === 'idle') {
            if (mi.button === MouseButtons.Auxiliary) return true;
            if (mi.button === MouseButtons.Secondary) return true;
            // if (this.targeting.getTopAsOverlayRegion()) return true;
            if (!this.isSelectExpected(mi) || this.config.hasPriority) return true;
        }
        return false;
    }

    private doPanBegin(ptrId: number, vpt: Point) {
        const { camera } = this;
        this.ptrId = ptrId;
        this.ptrStart = vpt;
        this.camPositionStart = camera.position;
        return true;
    }

    private doPanContinue(ptrId: number, vpt: Point) {
        const { dispatch, camera } = this;
        if (this.ptrId !== ptrId) return false;
        // In special cases, we start without a ptrStart, so default to current view point
        if (!this.ptrStart) {
            this.ptrStart = vpt;
        }
        const delta = this.ptrStart.subtract(vpt).divide(camera.scale);
        dispatch(cameraMoveTo, this.camPositionStart!.add(delta).toArray());
        this.isPanning = true;
        return true;
    }

    private doPanEnd(ptrId: number) {
        if (this.ptrId !== ptrId) return false;
        this.doReset();
        return true;
    }

    private doReset(): void {
        this.ptrId = null;
        this.ptrStart = null;
        this.camPositionStart = null;
        this.isPanning = false;
        // this.zScaleAt = null;
        // this.zScaleLast = null;
    }

    private isSelectExpected(mi: MouseInput) {
        const [hit] = this.interaction.testFor('select', mi.modifiers.alt);
        return !!hit;
    }
}

export const PanZoomToolBehavior = (config: PanZoomToolBehaviorConfig = {}) =>
    class extends AbstractPanZoomToolBehavior {
        constructor() {
            super(config);
        }
    };
